<template>
  <div class="p2p-page">
    <div class="p2p-between">
      <div class="p2p-stack" style="gap:6px">
        <h1 class="p2p-h1">Approvals</h1>
        <p v-if="reviewing" class="p2p-lead">You review autopilot posts for {{ reviewing.email }}. Approved posts go to their accounts and use their credits; you cannot edit posts or change their settings.</p>
        <p v-else class="p2p-lead">Autopilot posts wait here until you approve them. Anything not approved by its deadline is skipped, never published.</p>
      </div>
      <button v-if="tab === 'pending' && approvals.length > 1" type="button" class="p2p-btn" :disabled="busy" @click="decide(approvals.map(item => item.id), 'approve')">Approve all {{ approvals.length }}</button>
    </div>
    <label v-if="owners.length" class="p2p-label" style="max-width:360px">Approvals for
      <select :value="ownerId || ''" class="p2p-select" @change="setOwner($event.target.value)">
        <option value="">Your own posts</option>
        <option v-for="item in owners" :key="item.owner_id" :value="item.owner_id">{{ item.email }}</option>
      </select>
    </label>
    <div class="p2p-tabs" role="tablist">
      <button type="button" role="tab" :class="{ on: tab === 'pending' }" :aria-selected="tab === 'pending' ? 'true' : 'false'" @click="setTab('pending')">Waiting</button>
      <button type="button" role="tab" :class="{ on: tab === 'history' }" :aria-selected="tab === 'history' ? 'true' : 'false'" @click="setTab('history')">History</button>
    </div>
    <div v-if="error" class="p2p-alert bad">{{ error }}</div>
    <div v-if="notice" class="p2p-alert" role="status">{{ notice }}</div>
    <div v-if="loading" class="p2p-skeleton" style="height:240px"></div>
    <div v-else-if="!approvals.length" class="p2p-empty">
      <strong>{{ tab === 'pending' ? 'Nothing waiting' : 'No decisions yet' }}</strong>
      <span v-if="tab !== 'pending'">Approved, rejected and skipped posts appear here.</span>
      <span v-else-if="reviewing">Nothing from {{ reviewing.email }} is waiting for review. We let you know when there is.</span>
      <template v-else-if="workspace && workspace.next_autopilot_at">
        <span>The next autopilot post goes out {{ when(workspace.next_autopilot_at) }}. Its draft appears here {{ soon(workspace.next_autopilot_at) ? 'shortly' : 'about a day before' }}, and we let you know.</span>
      </template>
      <template v-else>
        <span>Turn on autopilot for a project and its posts wait here for your approval.</span>
        <router-link to="/" class="p2p-btn sm">Choose a project</router-link>
      </template>
    </div>
    <PostGrid v-else v-slot="{ cell }" :count="approvals.length">
      <div v-for="(item, index) in approvals" :key="item.id" class="p2p-stack" style="gap:10px" :style="cell(index).style">
        <div class="p2p-card tight raised">
          <div class="p2p-between">
            <span class="p2p-h3">{{ item.project_name }}</span>
            <span class="p2p-row" style="gap:4px"><NetBadge v-for="account in item.accounts" :key="account.id" :platform="account.platform" /></span>
          </div>
          <div class="p2p-faint">Publishes {{ when(item.planned_at) }}<span v-if="tab === 'pending'"> · decide by {{ when(item.due_at) }}</span></div>
          <div v-if="tab === 'pending' && publishCost(item)" class="p2p-faint">{{ publishCost(item) }}</div>
          <div v-if="item.reason && tab === 'pending'" class="p2p-alert">{{ item.reason }}</div>
          <div v-if="tab === 'history'" class="p2p-row" style="gap:8px"><span :class="['p2p-chip', tone(item.status)]">{{ label(item.status) }}</span>
            <span v-if="item.status === 'rejected' && item.reason" class="p2p-faint">{{ item.reason }}</span></div>
          <div v-else class="p2p-row">
            <button type="button" class="p2p-btn sm" :disabled="busy" @click="decide([item.id], 'approve')">Approve</button>
            <v-menu offset-y>
              <template #activator="{ on, attrs }">
                <button type="button" class="p2p-btn sm ghost" :disabled="busy" v-bind="attrs" v-on="on">Reject <v-icon small>mdi-chevron-down</v-icon></button>
              </template>
              <v-list dense>
                <v-list-item v-for="reason in reasons" :key="reason.key" @click="decide([item.id], 'reject', reason.key)"><v-list-item-title>{{ reason.label }}</v-list-item-title></v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>
        <PostCard v-if="item.post" :post="item.post" :readonly="tab === 'history' || !!reviewing" :platforms="item.accounts.map(account => account.platform)"
                  :prices="prices" :wide="cell(index).wide" @updated="updatePost(item, $event)" @operation="load" @delete="load" />
      </div>
    </PostGrid>
  </div>
</template>

<script>
import NetBadge from '../components/NetBadge.vue';
import PostCard from '../components/PostCard.vue';
import PostGrid from '../components/PostGrid.vue';
import { get, post, message } from '../api';
import { localDate } from '../poll';

export default {
  name: 'Approvals',
  components: { NetBadge, PostCard, PostGrid },
  data: () => ({ approvals: [], owners: [], loading: true, busy: false, error: '', notice: '',
    reasons: [{ key: '', label: 'Just reject it' }, { key: 'not_accurate', label: 'Not accurate' }, { key: 'off_tone', label: 'Not my tone' },
      { key: 'not_relevant', label: 'Not relevant' }, { key: 'bad_timing', label: 'Wrong timing' }] }),
  computed: {
    tab() { return this.$route.query.tab === 'history' ? 'history' : 'pending'; },
    ownerId() { return Number(this.$route.query.owner) || null; },
    reviewing() { return this.ownerId ? (this.owners.find(item => item.owner_id === this.ownerId) || { email: 'another member' }) : null; },
    prices() { const config = this.$store.getters['publisher/config']; return config ? config.prices : undefined; },
    workspace() { return this.$store.getters['publisher/workspace']; }
  },
  watch: { tab() { this.load(); }, ownerId() { this.load(); } },
  mounted() {
    this.$store.dispatch('publisher/loadConfig');
    get('/reviewers').then(data => { this.owners = data.reviewing_for; }).catch(() => {});
    this.load();
  },
  methods: {
    when(value) { return localDate(value); },
    // Drafts are written a day ahead, so a slot closer than that is being written now.
    soon(value) { return new Date(value).getTime() - Date.now() < 24 * 3600 * 1000; },
    // The writing is already paid for; approving adds one publish per account, charged when the network confirms it.
    publishCost(item) {
      const each = Number((this.prices || {}).publish || 0);
      const count = item.accounts.length;
      if (!each || !count) return '';
      const total = each * count;
      return 'Uses ' + total + ' credit' + (total === 1 ? '' : 's') + ' when it goes out (' + count + ' account' + (count === 1 ? '' : 's') + ').';
    },
    query(changes) {
      const next = { ...this.$route.query, ...changes };
      Object.keys(next).forEach(key => { if (!next[key]) delete next[key]; });
      return next;
    },
    setTab(key) { this.$router.replace({ query: this.query({ tab: key === 'history' ? key : '' }) }).catch(() => {}); },
    setOwner(value) { this.$router.replace({ query: this.query({ owner: value }) }).catch(() => {}); },
    label(status) { return { approved: 'Approved', rejected: 'Rejected', expired_skipped: 'Skipped (not approved in time)' }[status] || status; },
    tone(status) { return { approved: 'good', rejected: '', expired_skipped: 'warn' }[status] || ''; },
    async load() {
      this.loading = true;
      this.error = '';
      try { this.approvals = (await get('/approvals', { tab: this.tab, owner: this.ownerId || undefined })).approvals; }
      catch (error) { this.approvals = []; this.error = message(error); } finally { this.loading = false; }
    },
    updatePost(item, updated) { item.post = updated; },
    async decide(ids, decision, reason) {
      this.busy = true;
      this.error = '';
      this.notice = '';
      try {
        const result = await post('/approvals/decide', { ids, decision, reason: reason || '', owner: this.ownerId });
        const late = (result.late || []).length;
        if (late) this.notice = 'The deadline passed for ' + (late === 1 ? 'one post' : late + ' posts') + ', so ' + (late === 1 ? 'it was' : 'they were') + ' skipped. Nothing late is published; you can still publish ' + (late === 1 ? 'it' : 'them') + " from the project's Posts tab.";
        await this.load();
        this.$store.dispatch('publisher/loadWorkspace');
      } catch (error) {
        this.error = message(error);
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>
